import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BarChart3,
  BadgeCheck,
  Headphones,
  Layers,
  Rocket,
  ShieldCheck,
  Users,
} from "lucide-react";
import SpaceJetNavbar from "../components/SpaceJetNavbar";
import Reveal from "../components/Reveal";
import { ThemeProvider } from "../contexts/ThemeContext";

const fanLines = [
  { d: "M112 760 C312 676, 480 590, 664 470 S970 264, 1224 152", c: "#e2e8f0", o: 0.55, w: 1.1 },
  { d: "M96 640 C300 602, 480 548, 680 432 S980 248, 1224 152", c: "#93c5fd", o: 0.62, w: 1.15 },
  { d: "M140 900 C322 790, 506 654, 702 520 S1002 282, 1224 152", c: "#cbd5e1", o: 0.5, w: 1.05 },
  { d: "M84 488 C270 520, 448 504, 638 396 S958 214, 1224 152", c: "#7dd3fc", o: 0.58, w: 1.1 },
  { d: "M162 1000 C366 856, 520 720, 706 578 S1006 324, 1224 152", c: "#f8fafc", o: 0.4, w: 1 },
  { d: "M52 392 C270 430, 444 430, 626 352 S964 182, 1224 152", c: "#bfdbfe", o: 0.55, w: 1.1 },
  { d: "M120 274 C310 332, 474 336, 648 304 S948 182, 1224 152", c: "#e2e8f0", o: 0.45, w: 1 },
  { d: "M220 1080 C392 900, 536 744, 716 594 S1006 320, 1224 152", c: "#cbd5e1", o: 0.34, w: 0.98 },
  { d: "M28 874 C246 826, 438 756, 626 632 S944 350, 1224 152", c: "#93c5fd", o: 0.4, w: 0.95 },
  { d: "M232 38 C384 178, 498 258, 664 284 S974 214, 1224 152", c: "#f8fafc", o: 0.32, w: 0.92 },
  { d: "M74 1060 C262 960, 436 862, 628 730 S960 408, 1224 152", c: "#94a3b8", o: 0.32, w: 0.9 },
  { d: "M18 560 C226 576, 408 560, 592 484 S928 220, 1224 152", c: "#cbd5e1", o: 0.3, w: 0.86 },
  { d: "M310 60 C430 182, 552 294, 682 336 S958 246, 1224 152", c: "#60a5fa", o: 0.42, w: 0.98 },
  { d: "M376 1096 C484 930, 590 782, 714 638 S992 358, 1224 152", c: "#dbeafe", o: 0.34, w: 0.92 },
  { d: "M8 286 C224 350, 404 356, 588 316 S928 194, 1224 152", c: "#f8fafc", o: 0.28, w: 0.88 },
  { d: "M182 106 C344 194, 480 260, 652 286 S972 214, 1224 152", c: "#c24141", o: 0.22, w: 0.84 },
  { d: "M48 988 C250 914, 430 830, 626 692 S962 366, 1224 152", c: "#93c5fd", o: 0.28, w: 0.84 },
  { d: "M280 14 C406 114, 522 214, 666 270 S980 206, 1224 152", c: "#e2e8f0", o: 0.26, w: 0.82 },
];

const contourLines = [
  "M-80 860 C150 790, 280 800, 440 848 S820 980, 1700 820",
  "M-120 920 C110 846, 300 864, 468 910 S836 1040, 1720 904",
  "M-140 980 C82 920, 284 944, 456 988 S840 1118, 1740 982",
  "M-180 1040 C60 994, 260 1012, 438 1050 S826 1180, 1750 1068",
  "M-220 1106 C34 1074, 246 1094, 428 1138 S820 1268, 1760 1136",
  "M-260 1168 C10 1152, 230 1176, 424 1222 S818 1354, 1764 1200",
  "M120 948 C304 880, 476 878, 612 920 S902 1030, 1468 948",
  "M150 1020 C338 956, 500 952, 640 992 S916 1102, 1520 1032",
];

const featureItems = [
  {
    icon: ShieldCheck,
    title: "Vetted partners",
    description: "We start with fit: audience, offer, compliance, and the kind of traffic a brand can scale with confidence.",
  },
  {
    icon: Headphones,
    title: "Hands-on support",
    description: "Real people stay involved after launch with onboarding, feedback, and practical help when campaigns need it.",
  },
  {
    icon: BarChart3,
    title: "Transparent performance",
    description: "Clear reporting and careful optimization keep the work focused on quality traffic and revenue that lasts.",
  },
];

const qualityCards = [
  {
    icon: Users,
    label: "Partner quality",
    title: "Selective by design",
    description: "We work with advertisers and publishers who want a managed relationship, not just another open network login.",
  },
  {
    icon: ShieldCheck,
    label: "Traffic quality",
    title: "Fit over volume",
    description: "We care about audience match, compliance, and the campaign conditions that make traffic worth scaling.",
  },
  {
    icon: BadgeCheck,
    label: "Account support",
    title: "People stay involved",
    description: "Expect direct communication, thoughtful onboarding, and support that keeps the work moving.",
  },
  {
    icon: Layers,
    label: "Performance view",
    title: "Reporting with context",
    description: "We look beyond raw clicks and focus on the signals that actually help campaigns grow responsibly.",
  },
];

const partnerReasons = [
  {
    title: "For advertisers",
    items: [
      "Quality traffic matched to the right offer and audience",
      "Campaign review before launch so the setup starts clean",
      "Ongoing optimization with clear feedback from the team",
    ],
  },
  {
    title: "For publishers",
    items: [
      "Selective offers that respect your audience and traffic mix",
      "Direct help when you need funnel notes, postback guidance, or offer clarity",
      "A network relationship that focuses on real revenue, not busywork",
    ],
  },
];

const supportSteps = [
  {
    step: "01",
    title: "Review the fit",
    text: "We look at the offer, traffic source, and expectations before anything goes live.",
  },
  {
    step: "02",
    title: "Launch with support",
    text: "Onboarding, tracking, and initial setup get handled with a real person in the loop.",
  },
  {
    step: "03",
    title: "Optimize together",
    text: "We use reporting and partner feedback to refine what is working and trim what is not.",
  },
];

const SpaceJetNetwork = () => {
  return (
    <ThemeProvider>
      <div className="relative min-h-screen overflow-x-clip bg-[#050b17] text-slate-100 spacejet-landing-shell">
        <SpaceJetNavbar />

        <main className="relative z-10">
          <section className="relative overflow-hidden">
            <div className="pointer-events-none absolute inset-0">
              <div className="absolute left-[-10rem] top-[-6rem] h-[34rem] w-[34rem] rounded-full bg-blue-500/12 blur-3xl" />
              <div className="absolute right-[-8rem] top-[3rem] h-[30rem] w-[30rem] rounded-full bg-slate-500/10 blur-3xl" />
              <div className="absolute inset-x-0 bottom-0 h-[34rem] bg-[linear-gradient(180deg,rgba(5,11,23,0)_0%,rgba(5,11,23,0.55)_60%,rgba(5,11,23,0.96)_100%)]" />
            </div>

            <div className="mx-auto grid min-h-[39rem] max-w-[1800px] items-center gap-10 px-6 pb-8 pt-20 lg:min-h-[41rem] lg:grid-cols-[0.48fr_0.52fr] lg:px-20 lg:pb-12 lg:pt-16 xl:min-h-[43rem]">
              <div className="relative z-10 max-w-[46rem] lg:pl-2 xl:pl-4">
                <Reveal animation="spacejet-fade-up">
                  <div className="spacejet-page-kicker">
                    Managed performance network
                  </div>
                </Reveal>

                <Reveal animation="spacejet-fade-up" delay={120}>
                  <h1 className="spacejet-page-title mt-6 text-5xl font-semibold leading-[1.05] text-white sm:text-6xl lg:text-[4.7rem] xl:text-[5.05rem]">
                    Better partners.
                    <span className="block sm:whitespace-nowrap">
                      Better performance<span className="text-red-400">.</span>
                    </span>
                  </h1>
                </Reveal>

                <Reveal animation="spacejet-fade-up" delay={190}>
                  <p className="spacejet-page-copy mt-7 max-w-[34rem] text-[1.02rem] sm:text-[1.12rem]">
                    We help advertisers grow with quality traffic.
                    <br />
                    We help publishers grow their revenue.
                  </p>
                </Reveal>

                <Reveal animation="spacejet-fade-up" delay={260}>
                  <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center">
                    <Link
                      to="/signup"
                      className="spacejet-btn-primary group inline-flex min-w-[15.5rem] items-center justify-center gap-5 rounded-md px-7 py-4 text-[11px] font-semibold uppercase tracking-[0.18em] transition-transform duration-200 hover:-translate-y-0.5"
                    >
                      I&apos;M AN ADVERTISER
                      <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                    </Link>
                    <Link
                      to="/affiliates"
                      className="spacejet-btn-secondary group inline-flex min-w-[15.5rem] items-center justify-center gap-5 rounded-md px-7 py-4 text-[11px] font-semibold uppercase tracking-[0.18em] transition-colors duration-200 hover:bg-white/[0.04]"
                    >
                      I&apos;M A PUBLISHER
                      <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                    </Link>
                  </div>
                </Reveal>
              </div>

              <Reveal animation="spacejet-scale-in" delay={120}>
                <div className="relative -mr-16 min-h-[27rem] lg:min-h-[36rem] xl:-mr-24 xl:min-h-[39rem]">
                  <div className="pointer-events-none absolute inset-0">
                    <svg
                      viewBox="0 0 1600 1100"
                      className="absolute inset-0 h-full w-full overflow-visible"
                      fill="none"
                      aria-hidden="true"
                    >
                      <defs>
                        <radialGradient id="fan-glow" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(1218 166) rotate(90) scale(180)">
                          <stop offset="0" stopColor="#f8fafc" stopOpacity="0.94" />
                          <stop offset="0.18" stopColor="#cbd5e1" stopOpacity="0.88" />
                          <stop offset="0.45" stopColor="#93c5fd" stopOpacity="0.28" />
                          <stop offset="1" stopColor="#c24141" stopOpacity="0" />
                        </radialGradient>
                        <linearGradient id="fan-stroke" x1="20" y1="1040" x2="1224" y2="152" gradientUnits="userSpaceOnUse">
                          <stop offset="0" stopColor="#94a3b8" stopOpacity="0.04" />
                          <stop offset="0.42" stopColor="#cbd5e1" stopOpacity="0.44" />
                          <stop offset="0.72" stopColor="#f8fafc" stopOpacity="0.7" />
                          <stop offset="1" stopColor="#c24141" stopOpacity="0.58" />
                        </linearGradient>
                      </defs>

                      {fanLines.map((line, index) => (
                        <path
                          key={`${line.d}-${index}`}
                          d={line.d}
                          stroke={line.c}
                          strokeOpacity={line.o}
                          strokeWidth={line.w}
                          strokeLinecap="round"
                          pathLength="1"
                          className={`spacejet-fan-line ${index % 4 === 0 ? 'spacejet-fan-line--ghost' : ''}`}
                          style={{ '--spacejet-fan-delay': `${index * 55}ms` }}
                        />
                      ))}

                      <path
                        d="M96 720 C284 620, 486 520, 670 408 S970 238, 1224 152"
                        stroke="url(#fan-stroke)"
                        strokeOpacity="0.76"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        pathLength="1"
                        className="spacejet-fan-line"
                        style={{ '--spacejet-fan-delay': '85ms' }}
                      />
                      <path
                        d="M64 530 C258 520, 434 480, 620 390 S956 218, 1224 152"
                        stroke="url(#fan-stroke)"
                        strokeOpacity="0.48"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        pathLength="1"
                        className="spacejet-fan-line spacejet-fan-line--ghost"
                        style={{ '--spacejet-fan-delay': '140ms' }}
                      />
                      <path
                        d="M132 860 C330 748, 510 612, 692 484 S990 286, 1224 152"
                        stroke="url(#fan-stroke)"
                        strokeOpacity="0.58"
                        strokeWidth="1.45"
                        strokeLinecap="round"
                        pathLength="1"
                        className="spacejet-fan-line"
                        style={{ '--spacejet-fan-delay': '190ms' }}
                      />

                      <circle cx="1224" cy="152" r="126" fill="url(#fan-glow)" className="spacejet-fan-node" />
                      <circle cx="1224" cy="152" r="18" fill="#f8fafc" opacity="0.98" className="spacejet-fan-node" />
                      <circle cx="1224" cy="152" r="7" fill="#c24141" className="spacejet-fan-core" />
                      <circle cx="1224" cy="152" r="68" stroke="rgba(226,232,240,0.16)" strokeWidth="1" className="spacejet-fan-node-ring" />
                      <circle cx="1224" cy="152" r="106" stroke="rgba(96,165,250,0.12)" strokeWidth="1" strokeDasharray="8 10" className="spacejet-fan-node-ring" />
                    </svg>
                  </div>
                </div>
              </Reveal>
            </div>
          </section>

          <section className="relative border-y border-white/[0.06] bg-[#07111f]/78 backdrop-blur-xl">
            <div className="pointer-events-none absolute inset-0 overflow-hidden">
              <svg
                viewBox="0 0 1800 760"
                className="spacejet-topographic absolute inset-0 h-full w-full"
                fill="none"
                aria-hidden="true"
              >
                {contourLines.map((d, index) => (
                  <path
                    key={`${d}-${index}`}
                    d={d}
                    stroke={index % 3 === 0 ? "rgba(96,165,250,0.1)" : index % 3 === 1 ? "rgba(226,232,240,0.08)" : "rgba(194,65,65,0.05)"}
                    strokeWidth={1.1}
                  />
                ))}
              </svg>
              <div className="absolute inset-0 bg-[radial-gradient(120%_140%_at_50%_120%,rgba(15,23,42,0)_28%,rgba(5,11,23,0.5)_72%,rgba(5,11,23,0.88)_100%)]" />
            </div>

            <div className="mx-auto grid max-w-[1800px] gap-0 px-6 py-7 lg:grid-cols-3 lg:px-20">
              {featureItems.map((item, index) => {
                const Icon = item.icon;
                return (
                  <Reveal key={item.title} animation="spacejet-fade-up" delay={index * 90}>
                    <div className={`flex items-start gap-8 py-8 ${index < featureItems.length - 1 ? 'border-b border-white/[0.06] lg:border-b-0 lg:border-r lg:border-white/[0.08] lg:pr-12' : ''} ${index === 0 ? 'lg:pl-16' : 'lg:pl-14'} ${index === 2 ? 'lg:justify-start' : ''}`}>
                      <div className="flex h-20 w-20 shrink-0 items-center justify-center text-red-400/90">
                        <Icon className="h-16 w-16" strokeWidth={1.45} />
                      </div>
                      <div>
                        <div className="text-base font-semibold uppercase tracking-[0.1em] text-white">{item.title}</div>
                        <p className="mt-4 max-w-[22rem] text-base leading-8 text-slate-300/88">{item.description}</p>
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </section>

          <section id="about" className="relative overflow-hidden border-b border-white/[0.05] py-20 lg:py-24">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_100%_at_50%_0%,rgba(30,41,59,0.18)_0%,rgba(5,11,23,0)_56%),linear-gradient(180deg,rgba(5,11,23,0.2)_0%,rgba(5,11,23,0.82)_100%)]" />
            <div className="mx-auto max-w-[1800px] px-6 lg:px-20">
              <Reveal animation="spacejet-fade-up">
                <div className="spacejet-page-kicker">
                  Partner quality
                </div>
              </Reveal>

              <div className="mt-6 grid gap-6 lg:grid-cols-2 xl:grid-cols-4">
                {qualityCards.map((card, index) => {
                  const Icon = card.icon;
                  return (
                    <Reveal key={card.title} animation="spacejet-scale-in" delay={index * 90}>
                      <div className="spacejet-surface-card h-full rounded-2xl p-6">
                        <div className="flex items-center justify-between">
                          <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-red-500/20 bg-red-500/8 text-red-200">
                            <Icon className="h-6 w-6" />
                          </div>
                          <div className="text-[11px] font-semibold uppercase tracking-[0.3em] text-slate-400">{card.label}</div>
                        </div>
                        <h2 className="spacejet-page-title mt-6 text-2xl font-semibold text-white">{card.title}</h2>
                        <p className="mt-4 text-[0.98rem] leading-7 text-slate-300">{card.description}</p>
                      </div>
                    </Reveal>
                  );
                })}
              </div>
            </div>
          </section>

          <section className="relative overflow-hidden border-b border-white/[0.05] py-20 lg:py-24">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(80%_80%_at_0%_0%,rgba(59,130,246,0.12)_0%,rgba(5,11,23,0)_52%),radial-gradient(70%_70%_at_100%_0%,rgba(148,163,184,0.08)_0%,rgba(5,11,23,0)_48%)]" />
            <div className="mx-auto max-w-[1800px] px-6 lg:px-20">
              <Reveal animation="spacejet-fade-up">
                <div className="spacejet-page-kicker">
                  Why partner with SpaceJet
                </div>
              </Reveal>

              <div className="mt-8 grid gap-8 xl:grid-cols-[1.08fr_0.92fr]">
                <Reveal animation="spacejet-fade-up" delay={120}>
                  <div className="spacejet-surface-card rounded-[1.75rem] p-8 lg:p-10">
                    <h2 className="spacejet-page-title text-3xl font-semibold leading-tight text-white sm:text-4xl">
                      Built for advertisers who want quality traffic and publishers who want revenue that can scale.
                    </h2>
                    <p className="spacejet-page-copy mt-5 max-w-2xl text-[1.02rem]">
                      SpaceJet is a managed performance network. We keep the process selective, the feedback direct, and the work focused on campaigns that deserve more than a passive approval.
                    </p>

                    <div className="mt-10 grid gap-4 md:grid-cols-2">
                      {partnerReasons.map((group) => (
                        <div key={group.title} className="rounded-2xl border border-white/[0.07] bg-white/[0.03] p-5">
                          <div className="text-[11px] font-semibold uppercase tracking-[0.34em] text-red-200/90">{group.title}</div>
                          <ul className="mt-4 space-y-3 text-slate-300">
                            {group.items.map((item) => (
                              <li key={item} className="flex gap-3 leading-7">
                                <span className="mt-2 h-1.5 w-1.5 rounded-full bg-red-400/90" />
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                </Reveal>

                <Reveal animation="spacejet-scale-in" delay={170}>
                  <div className="spacejet-surface-card rounded-[1.75rem] p-8 lg:p-10">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <div className="text-[11px] font-semibold uppercase tracking-[0.34em] text-slate-400">Managed support</div>
                        <h3 className="spacejet-page-title mt-3 text-2xl font-semibold text-white">Optimization with a human point of contact.</h3>
                      </div>
                      <div className="rounded-full border border-red-500/20 bg-red-500/8 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.26em] text-red-200">
                        Active
                      </div>
                    </div>

                    <div className="mt-8 space-y-4">
                      {supportSteps.map((item) => (
                        <div key={item.step} className="flex gap-4 rounded-2xl border border-white/[0.06] bg-[#07111f]/60 p-5">
                          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-slate-600/70 bg-slate-800/70 font-display text-base text-red-200">
                            {item.step}
                          </div>
                          <div>
                            <div className="text-base font-semibold text-white">{item.title}</div>
                            <p className="mt-2 text-sm leading-7 text-slate-300">{item.text}</p>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="mt-8 rounded-2xl border border-blue-400/15 bg-blue-500/5 p-5 text-sm leading-7 text-slate-300">
                      Advertisers get better traffic decisions. Publishers get clearer offer fit. Both sides get a team that pays attention after launch.
                    </div>
                  </div>
                </Reveal>
              </div>
            </div>
          </section>

          <section id="resources" className="relative overflow-hidden py-20 lg:py-24">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(75%_100%_at_50%_100%,rgba(220,38,38,0.12)_0%,rgba(5,11,23,0)_62%)]" />
            <div className="mx-auto max-w-[1800px] px-6 lg:px-20">
              <div className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
                <Reveal animation="spacejet-fade-up">
                  <div className="spacejet-surface-card rounded-[1.75rem] p-8 lg:p-10">
                    <div className="spacejet-page-kicker">Managed support</div>
                    <h2 className="spacejet-page-title mt-4 text-3xl font-semibold text-white sm:text-4xl">
                      Support that is built into the relationship.
                    </h2>
                    <p className="spacejet-page-copy mt-5 text-[1.02rem]">
                      We are set up to help advertisers launch cleanly and help publishers stay aligned with offers that make sense for their traffic. That means clearer feedback, faster answers, and fewer dead ends.
                    </p>
                  </div>
                </Reveal>

                <Reveal animation="spacejet-scale-in" delay={120}>
                  <div className="spacejet-surface-card rounded-[1.75rem] p-8 lg:p-10">
                    <div className="grid gap-4 sm:grid-cols-2">
                      {[
                        {
                          title: "Campaign setup",
                          text: "We help align goals, tracking, and launch details before spend starts moving.",
                        },
                        {
                          title: "Partner fit",
                          text: "We keep the focus on traffic and offers that make sense for the audience in front of us.",
                        },
                        {
                          title: "Ongoing reviews",
                          text: "When something shifts, we look at the numbers and the context, not just the dashboard.",
                        },
                        {
                          title: "Practical guidance",
                          text: "You get direct feedback on what to adjust, what to keep, and what should be cut.",
                        },
                      ].map((item) => (
                        <div key={item.title} className="rounded-2xl border border-white/[0.06] bg-white/[0.03] p-5">
                          <div className="text-sm font-semibold uppercase tracking-[0.28em] text-white">{item.title}</div>
                          <p className="mt-3 text-sm leading-7 text-slate-300">{item.text}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </Reveal>
              </div>
            </div>
          </section>

          <section className="relative border-t border-white/[0.05] bg-[#07111f]/88 py-20 lg:py-24">
            <div className="mx-auto max-w-[1800px] px-6 text-center lg:px-20">
              <Reveal animation="spacejet-fade-up">
                <div className="spacejet-page-kicker">Start here</div>
                <h2 className="spacejet-page-title mt-4 text-4xl font-semibold text-white sm:text-5xl">
                  Talk to SpaceJet about fit, traffic, and growth.
                </h2>
                <p className="spacejet-page-copy mx-auto mt-5 max-w-3xl text-lg">
                  If you are looking for a managed performance network that keeps the work personal and the goals clear, we are ready to talk.
                </p>
              </Reveal>

              <Reveal animation="spacejet-fade-up" delay={120}>
                <div className="mt-10 flex flex-col justify-center gap-4 sm:flex-row">
                  <Link
                    to="/signup"
                    className="spacejet-btn-primary group inline-flex items-center justify-center gap-4 rounded-md px-8 py-4 text-[11px] font-semibold uppercase tracking-[0.18em] transition-transform duration-200 hover:-translate-y-0.5"
                  >
                    Request partnership review
                    <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                  </Link>
                  <Link
                    to="/contact"
                    className="spacejet-btn-secondary inline-flex items-center justify-center rounded-md px-8 py-4 text-[11px] font-semibold uppercase tracking-[0.18em] transition-colors duration-200 hover:bg-white/[0.04]"
                  >
                    Contact the team
                  </Link>
                </div>
              </Reveal>
            </div>
          </section>
        </main>

        <footer className="border-t border-white/[0.05] bg-[#050b17]/95 py-10">
          <div className="mx-auto flex max-w-[1800px] flex-col gap-6 px-6 lg:flex-row lg:items-center lg:justify-between lg:px-20">
            <div className="flex items-center gap-3 text-slate-300">
              <Rocket className="h-4 w-4 text-red-300" />
              <span className="text-[11px] uppercase tracking-[0.26em]">SpaceJet Network</span>
            </div>
            <div className="flex flex-wrap items-center gap-x-8 gap-y-3 text-[11px] uppercase tracking-[0.28em] text-slate-500">
              <Link to="/advertisers" className="transition-colors hover:text-slate-300">Advertisers</Link>
              <Link to="/affiliates" className="transition-colors hover:text-slate-300">Publishers</Link>
              <Link to="/contact" className="transition-colors hover:text-slate-300">Contact</Link>
              <span>© 2026 SpaceJet Network</span>
            </div>
          </div>
        </footer>
      </div>
    </ThemeProvider>
  );
};

export default SpaceJetNetwork;
